import { Link2, ShieldCheck, Loader2, LogOut } from "lucide-react";
import { useState } from "react";

/**
 * Read-only session/Assos365 link state card.
 * `me` is the response from GET /api/auth/me, or null when unauthenticated.
 *
 * The "Link Assos365" CTA is intentionally a non-functional placeholder
 * for AP-4D (the link callback page is AP-4E). Tooltip explains that
 * we're not wiring it yet.
 */
export default function SessionState({ me, loading, onLogout }) {
  if (loading) {
    return (
      <div
        data-testid="redeem-session-state-loading"
        className="glass rounded-md p-5 flex items-center gap-2 text-white/60"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="font-mono text-xs">checking session…</span>
      </div>
    );
  }

  if (!me) {
    return (
      <div
        data-testid="redeem-session-state-unlinked"
        className="glass rounded-md p-5"
      >
        <div className="flex items-start gap-3">
          <Link2 className="mt-1 h-4 w-4 text-[#00F0FF]" />
          <div className="flex-1">
            <h3 className="font-display text-sm uppercase tracking-[0.2em] text-white">
              Assos365 not linked
            </h3>
            <p className="mt-1 font-mono text-[11px] leading-relaxed text-white/60">
              Linking starts from Assos365. Open your Assos365 account and
              follow the "Link AssosPay" option — Assos365 will redirect
              you back here automatically with a one-time link.
            </p>
            <p
              data-testid="redeem-link-cta"
              data-cta-state="preview-helper"
              title="Linking is initiated from Assos365 — there is no AssosPay-side button to start it."
              className="mt-3 inline-flex items-center gap-2 rounded border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#00F0FF]"
            >
              Link starts on Assos365
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="redeem-session-state-linked"
      data-arc-user-id={me.arc_user_id}
      className="glass rounded-md p-5"
    >
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-1 h-4 w-4 text-[#39FF14]" />
        <div className="flex-1">
          <h3 className="font-display text-sm uppercase tracking-[0.2em] text-white">
            Linked to Assos365
          </h3>
          <p className="mt-1 font-mono text-[11px] text-white/60">
            Session active · Assos365 ID
            <span className="ml-1 text-white/85" data-testid="redeem-session-iuid-last4">
              ●●●●{me.internal_user_id_last4 || "----"}
            </span>
          </p>
          <p className="mt-1 font-mono text-[10px] text-white/40">
            arc_user_id&nbsp;·&nbsp;<span className="text-white/60">{me.arc_user_id}</span>
          </p>
          {me.credit_pilot_eligible ? (
            <p
              data-testid="redeem-credit-pilot-badge"
              data-credit-pilot-eligible="true"
              title="You are on the AP-4H direct-credit pilot allowlist."
              className="mt-2 inline-flex items-center gap-2 rounded border border-[#39FF14]/40 bg-[#39FF14]/5 px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#39FF14]"
            >
              Direct-credit pilot
            </p>
          ) : null}
        </div>
        <LogoutButton onLogout={onLogout} />
      </div>
    </div>
  );
}

/**
 * Tiny encapsulated logout button. Calls the parent's `onLogout`
 * handler (which owns the actual `POST /api/auth/logout`). Disables
 * itself while in flight so a double-click can't fire two logouts.
 */
function LogoutButton({ onLogout }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    if (busy || !onLogout) return;
    setBusy(true);
    try {
      await onLogout();
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      data-testid="redeem-session-logout-btn"
      data-busy={busy ? "true" : "false"}
      title="End your AssosPay session. Your wallet connection (if any) is unaffected."
      className="shrink-0 inline-flex items-center gap-1.5 rounded border border-white/15 bg-black/40 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-white/70 hover:text-[#FF3B30] hover:border-[#FF3B30]/40 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
    >
      {busy ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <LogOut className="h-3 w-3" />
      )}
      {busy ? "ending…" : "log out"}
    </button>
  );
}
